import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type {
  IChannelVo,
  IContextHomeTotalVo,
  IResChannelList,
  IResStatisticsChartList,
  IResStatisticsContentsList,
  IStatisticsContentsGridVo,
  IStatisticsDateGridVo,
  StatisticsTabType,
} from "./StatisticsVo";
import type { IPageInfo } from "pages/notice/store/NoticeVo";
import { createAppThunk, type IRes } from "store/modules/common";
import Http from "commons/Http";
import moment from "moment";

// 이름 설정
const name = "statistics";

export interface IStatistics {
  activeTab?: StatisticsTabType;
  activePpService?: string;
  channelList?: { id: number; text: string; value: string | number }[];
  selectChannelList: { [key: string]: { code: number; text: string }[] };
  homeTotal: {
    provider?: IContextHomeTotalVo;
  };
  date: {
    list: IStatisticsDateGridVo[];
    page_info: IPageInfo;
  };
  contents: {
    list: IStatisticsContentsGridVo[];
    page_info: IPageInfo;
  };
  pp: {
    list: IStatisticsDateGridVo[];
  };
  chart: {
    total_page_view: number;
    total_revenue?: number;
    total_premium_page_view?: number;

    date?: Array<string>;
    page_views?: Array<number>;
    revenue?: Array<number>;
    premium_page_views?: Array<number>;
  };
  isLoading: {
    chart: boolean;
    content_list: boolean;
    date_list: boolean;
    pp_list?: boolean;
  };
}
export function toSort(o?: { [key: string]: string }) {
  if (o) {
    const key = Object.keys(o);
    if (key.length) {
      return `${key[0]},${o[key[0]].toLowerCase()}`;
    }
  }
  return undefined;
}

// 계정 별 묶여있는 채널 리스트 조회
export const apiGChannelList = createAppThunk<IResChannelList, { type?: StatisticsTabType; subType?: string }>(
  `${name}/apiGPartnerList`,
  async (param, thunkApi) => {
    const type = param.type ? param.type.toLocaleUpperCase() : null;
    const subType = param.subType ? param.subType.toLocaleUpperCase() : null;

    const res = await Http.get<IRes<IResChannelList>>("codebook/service", {
      params: type === "PROVIDER" && subType !== undefined ? { type: type, subType: subType } : { type: type },
    });
    if (res.data.content) {
      thunkApi.dispatch(rdxSetChannelList(res.data.content.data.list));
    }
    return res.data;
  },
);

/**
 * 통계 - 날짜별 차트, 조회수 그리드 데이터 조회
 */
export const apiGStatisticsDateGridData = createAppThunk<
  { data: IResStatisticsContentsList },
  { page: number; tab: StatisticsTabType }
>(`${name}/apiGStatisticsDateGridData`, async (option: { page: number; tab: StatisticsTabType }, thunkApi) => {
  const tab = option.tab;
  const param = {
    page: option.page !== undefined ? option.page : thunkApi.getState().statistics.date.page_info.page,
    size: thunkApi.getState().statistics.date.page_info.size,
    start: thunkApi.getState().ui?.inputText?.[`${tab}_DateOption_start`],
    end: thunkApi.getState().ui?.inputText?.[`${tab}_DateOption_end`],

    //기존 사용하던 idList
    // idList:
    //   Object.keys(thunkApi.getState().statistics.selectChannelList).length > 0
    //     ? thunkApi.getState().statistics.selectChannelList[tab!].map(i => i.code)
    //     : [],
    idList: thunkApi.getState().navigates.selectChannel
      ? [thunkApi.getState().navigates.selectChannel?.value]
      : undefined,
    serviceType: tab,
    platformCode: thunkApi.getState().statistics.activePpService
      ? thunkApi.getState().statistics.activePpService
      : null,
    // sort: toSort(thunkApi.getState().ui?.gridOption?.["reportGrid"]?.gridSort),
  };

  const res = await Http.get<IRes<{ data: IResStatisticsContentsList }>>("statistics/info", {
    params: param,
  });

  if (res.data.content) {
    thunkApi.dispatch(rdxSetDateGridlList(res.data.content.data.list as IStatisticsDateGridVo[]));
    thunkApi.dispatch(
      rdxSetDatePageInfo({
        page: res.data.content.data.page_info.page,
        size: res.data.content.data.page_info.size,
        total_elements: res.data.content.data.page_info.total_elements,
        totalPage: Math.ceil(res.data.content.data.page_info.total_elements! / res.data.content.data.page_info.size!),
      }),
    );
  }

  return res.data;
});
/**
 * 통계 - 날짜별 차트관련 PP데이터 리스트
 */
export const apiGStatisticsPPlist = createAppThunk<{ data: IResStatisticsContentsList }, undefined>(
  `${name}/apiGStatisticsPPlist`,
  async (_, thunkApi) => {
    // const tab = thunkApi.getState().statistics.activeTab
    //   ? thunkApi.getState().statistics.activeTab
    //   : thunkApi.getState().auth.service_type
    //   ? (thunkApi.getState().auth.service_type![0] as StatisticsTabType)
    //   : "PROVIDER";
    const tab = "PROVIDER";
    const param = {
      page: 0,
      size: 50,
      start: thunkApi.getState().ui?.inputText?.[`${tab}_DateOption_start`],
      end: thunkApi.getState().ui?.inputText?.[`${tab}_DateOption_end`],

      //기존 사용하던 idList
      // idList:
      // Object.keys(thunkApi.getState().statistics.selectChannelList).length > 0
      //   ? thunkApi.getState().statistics.selectChannelList[tab!].map(i => i.code)
      //   : [],
      idList: thunkApi.getState().navigates.selectChannel
        ? [thunkApi.getState().navigates.selectChannel?.value]
        : undefined,

      serviceType: tab,
    };

    const res = await Http.get<IRes<{ data: IResStatisticsContentsList }>>("statistics/platform", {
      params: param,
    });

    if (res.data.content) {
      thunkApi.dispatch(rdxSetPPDatalList(res.data.content.data.list as IStatisticsDateGridVo[]));
    }

    return res.data;
  },
);

/**
 * 통계 - 컨텐츠 그리드 데이터 조회
 */
export const apiGStatisticsContentGridData = createAppThunk<
  { data: IResStatisticsContentsList },
  { page: number; tab: StatisticsTabType }
>(`${name}/apiGStatisticsContentGridData`, async (option: { page: number; tab: StatisticsTabType }, thunkApi) => {
  const tab = option.tab;
  const param = {
    page: option.page !== undefined ? option.page : thunkApi.getState().statistics.contents.page_info.page,
    size: thunkApi.getState().statistics.contents.page_info.size,
    start: thunkApi.getState().ui?.inputText?.[`${tab}_DateOption_start`],
    end: thunkApi.getState().ui?.inputText?.[`${tab}_DateOption_end`],
    // idList:
    //   Object.keys(thunkApi.getState().statistics.selectChannelList).length > 0
    //     ? thunkApi.getState().statistics.selectChannelList[tab!].map(i => i.code)
    //     : [],
    idList: thunkApi.getState().navigates.selectChannel
      ? [thunkApi.getState().navigates.selectChannel?.value]
      : undefined,
    serviceType: tab,
    platformCode: thunkApi.getState().statistics.activePpService
      ? thunkApi.getState().statistics.activePpService
      : null,
    // sort: toSort(thunkApi.getState().ui?.gridOption?.["reportGrid"]?.gridSort),
  };

  const res = await Http.get<IRes<{ data: IResStatisticsContentsList }>>("statistics/list", {
    params: param,
  });

  if (res.data.content) {
    thunkApi.dispatch(rdxSetContentGridlList(res.data.content.data.list as IStatisticsContentsGridVo[]));
    thunkApi.dispatch(
      rdxSetContentPageInfo({
        page: res.data.content.data.page_info.page,
        size: res.data.content.data.page_info.size,
        total_elements: res.data.content.data.page_info.total_elements,
        totalPage: Math.ceil(res.data.content.data.page_info.total_elements! / res.data.content.data.page_info.size!),
      }),
    );
  }

  return res.data;
});

/**
 * 통계 - 차트 데이터
 */
export const apiGStatisticsChartData = createAppThunk<IResStatisticsChartList, StatisticsTabType>(
  `${name}/apiGStatisticsChartData`,
  async (tab, thunkApi) => {
    const param = {
      start: thunkApi.getState().ui?.inputText?.[`${tab}_DateOption_start`],
      end: thunkApi.getState().ui?.inputText?.[`${tab}_DateOption_end`],
      // idList:
      //   Object.keys(thunkApi.getState().statistics.selectChannelList).length > 0
      //     ? thunkApi.getState().statistics.selectChannelList[tab!].map(i => i.code)
      //     : [],
      idList: thunkApi.getState().navigates.selectChannel
        ? [thunkApi.getState().navigates.selectChannel?.value]
        : undefined,
      serviceType: tab,
      platformCode: thunkApi.getState().statistics.activePpService
        ? thunkApi.getState().statistics.activePpService
        : null,
      // sort: toSort(thunkApi.getState().ui?.gridOption?.["reportGrid"]?.gridSort),
    };

    const res = await Http.get<IRes<IResStatisticsChartList>>("statistics/chart", {
      params: param,
    });

    if (res.data.content) {
      thunkApi.dispatch(rdxSetChartList(res.data.content));
    }

    return res.data;
  },
);

export const apiGcontextHomeTotal = createAppThunk(`${name}/apiGcontextHomeTotal`, async (_, thunkApi) => {
  const param = {
    platformCode: thunkApi.getState().statistics.activePpService
      ? thunkApi.getState().statistics.activePpService
      : null,
  };
  const res = await Http.get("statistics/total", { params: param });

  if (res.data.content) {
    thunkApi.dispatch(rdxSetContextHomeTotal(res.data.content));
  }

  return res.data;
});

const statisticsSlice = createSlice({
  name,
  initialState: {
    channelList: [],
    selectChannelList: {},
    homeTotal: {},
    date: {
      list: [],
      page_info: {
        total_elements: 0,
        page: 0,
        size: 20,
        totalPage: 0,
      },
    },
    contents: {
      list: [],
      page_info: {
        total_elements: 0,
        page: 0,
        size: 10,
        totalPage: 0,
      },
    },
    pp: { list: [] },
    chart: { total_page_view: 0 },
    isLoading: { chart: false, content_list: false, date_list: false, pp_list: false },
  } as IStatistics,
  reducers: {
    rdxResetDateData(state: IStatistics) {
      delete state.activeTab;
      delete state.activePpService;
      state.homeTotal = {};
      state.selectChannelList = {};
      state.date.list = [];
      state.date.page_info = {
        total_elements: 0,
        page: 0,
        size: 20,
        totalPage: 0,
      };
    },
    rdxResetContentData(state: IStatistics) {
      state.contents.list = [];
      state.contents.page_info = {
        total_elements: 0,
        page: 0,
        size: 10,
        totalPage: 0,
      };
    },
    rdxResetChartData(state: IStatistics) {
      state.chart.total_page_view = 0;
      delete state.chart.date;
      delete state.chart.page_views;
      delete state.chart.revenue;
      delete state.chart.total_revenue;

      state.pp.list = [];
    },

    //pp select 삭제
    rdxResetPpSelect(state: IStatistics) {
      delete state.activePpService;
    },

    rdxSetStatisticsActiveTab(state: IStatistics, action: PayloadAction<StatisticsTabType>) {
      state.activeTab = action.payload;
      state.selectChannelList = {};
    },

    //통계 - 콘텐츠 그리드 데이터 저장
    rdxSetContentGridlList(state: IStatistics, action: PayloadAction<IStatisticsContentsGridVo[]>) {
      state.contents.list = action.payload;
    },
    //통계 - 날짜별 그리드 데이터 저장
    rdxSetDateGridlList(state: IStatistics, action: PayloadAction<IStatisticsDateGridVo[]>) {
      state.date.list = action.payload;
    },
    //통계 - 차트 우측 pp리스트
    rdxSetPPDatalList(state: IStatistics, action: PayloadAction<IStatisticsDateGridVo[]>) {
      state.pp.list = action.payload;
    },
    //통계 - 차트
    rdxSetChartList(state: IStatistics, action: PayloadAction<IResStatisticsChartList>) {
      state.chart = {
        total_page_view: action.payload.page_views,
        total_revenue: action.payload.expected_revenue,
        total_premium_page_view:
          action.payload.premium_page_view !== undefined ? action.payload.premium_page_view : undefined,

        date: action.payload.daily_page_views.map(item => moment(item.date).format("YYYY. MM. DD")).reverse(),
        page_views: action.payload.daily_page_views.map(item => item.value).reverse(),
        revenue: action.payload.daily_expected_revenues!.map(item => item.value).reverse(),

        premium_page_views:
          action.payload.premium_page_views !== undefined
            ? action.payload.premium_page_views.map(item => item.value).reverse()
            : undefined,
      };
    },

    //통계 - 날짜별 페이지 인포
    rdxSetDatePageInfo(state: IStatistics, action: PayloadAction<IPageInfo>) {
      state.date.page_info = action.payload;
    },
    //통계 - 콘텐츠 페이지 인포
    rdxSetContentPageInfo(state: IStatistics, action: PayloadAction<IPageInfo>) {
      state.contents.page_info = action.payload;
    },
    //통계 - 옵션 채널 리스트
    rdxSetChannelList(state: IStatistics, action: PayloadAction<IChannelVo[]>) {
      state.channelList = action.payload.map((i, idx) => {
        return { id: idx, text: i.name, value: i.id };
      });
    },
    //통계 - 선택한 채널 리스트
    rdxSetSelectChannelList(state: IStatistics, action: PayloadAction<{ tab: string; code: number; text: string }>) {
      const tab = state.activeTab ? state.activeTab : action.payload.tab;
      const empty = Object.keys(state.selectChannelList).length > 0 ? false : true;
      const list = empty ? [] : state.selectChannelList[`${tab}`].map(i => i.code);
      if (!list.includes(action.payload.code)) {
        if (empty) {
          state.selectChannelList = {
            [tab!]: [
              {
                code: action.payload.code,
                text: action.payload.text,
              },
            ],
          };
        } else {
          state.selectChannelList[`${tab}`] = state.selectChannelList[`${tab}`].concat(action.payload);
        }
      }
    },

    //콘텍스트 - 홈(통계) - 토탈 데이터
    rdxSetContextHomeTotal(state: IStatistics, action: PayloadAction<IContextHomeTotalVo>) {
      state.homeTotal.provider = action.payload;
    },

    //콘텍스트 - 홈(통계) - pp select
    rdxSetContextPpSelect(state: IStatistics, action: PayloadAction<string>) {
      state.activePpService = action.payload;
    },

    rdxRomoveSelectChannelList(state: IStatistics, action: PayloadAction<{ tab: StatisticsTabType; code: number }>) {
      const tab = state.activeTab ? state.activeTab : action.payload.tab;

      const list = state.selectChannelList[tab];
      const removeTarget = list!.filter(item => {
        return item.code !== action.payload.code;
      });
      state.selectChannelList[tab] = removeTarget;
    },
  },
  extraReducers: builder => {
    builder.addCase(apiGStatisticsChartData.pending, state => {
      state.isLoading.chart = true;
    });
    builder.addCase(apiGStatisticsChartData.fulfilled, state => {
      state.isLoading.chart = false;
    });

    builder.addCase(apiGStatisticsContentGridData.pending, state => {
      state.isLoading.content_list = true;
    });
    builder.addCase(apiGStatisticsContentGridData.fulfilled, state => {
      state.isLoading.content_list = false;
    });

    builder.addCase(apiGStatisticsDateGridData.pending, state => {
      state.isLoading.date_list = true;
    });
    builder.addCase(apiGStatisticsDateGridData.fulfilled, state => {
      state.isLoading.date_list = false;
    });
  },
});

export const {
  rdxResetDateData,
  rdxResetContentData,
  rdxResetChartData,

  rdxSetChannelList,
  rdxSetContentGridlList,
  rdxSetDateGridlList,
  rdxSetPPDatalList,
  rdxSetChartList,

  rdxSetContentPageInfo,
  rdxSetDatePageInfo,
  rdxSetStatisticsActiveTab,
  rdxSetSelectChannelList,
  rdxRomoveSelectChannelList,

  //new 홈
  rdxSetContextHomeTotal,
  rdxSetContextPpSelect,
  rdxResetPpSelect,
} = statisticsSlice.actions;

export default statisticsSlice.reducer;
